import classNames from "classnames";
import { useState } from "react";
import AscSelectInput from "../AscInputFields/AscSelectInput/AscSelectInput";
import styles from "./AscCrosswordPuzzle.module.scss";
import {
  AscCrosswordPuzzleProps,
  AscCrosswordPuzzleWordCreate,
  AscCrosswordPuzzleWordResponse,
  AscCrosswordPuzzleWordSolve,
  AscCrosswordPuzzleBoardSize,
  AscCrosswordPuzzleWordScope,
  ignoreAttribute,
} from "@ascendnow/shared";
import AscCrosswordPuzzleBoard from "./AscCrosswordPuzzleBoard/AscCrosswordPuzzleBoard";
import AscCrosswordPuzzleClueList from "./AscCrosswordPuzzleClueList/AscCrosswordPuzzleClueList";

export default function AscCrosswordPuzzle({
  className,
  wordsAcross,
  wordsDown,
  mode = "solve",
  size = AscCrosswordPuzzleBoardSize.TEN_BY_TEN,
  disabled = false,
  onGenerate,
  onSolve,
  onError,
}: AscCrosswordPuzzleProps) {
  const [createModeSize, setCreateModeSize] =
    useState<AscCrosswordPuzzleBoardSize>(size);
  const [generateWords, setGenerateWords] =
    useState<
      AscCrosswordPuzzleWordResponse<
        Required<AscCrosswordPuzzleWordCreate & AscCrosswordPuzzleWordSolve>
      >
    >();

  function handleOnChange(wordResponse: AscCrosswordPuzzleWordResponse) {
    onSolve?.(wordResponse);
  }

  function handleOnSizeChange(size: AscCrosswordPuzzleBoardSize | undefined) {
    if (!size) {
      return;
    }
    setCreateModeSize(size);
  }

  function handleOnGenerate(
    words: AscCrosswordPuzzleWordResponse<
      Required<AscCrosswordPuzzleWordScope>
    >,
    error = false
  ) {
    const generateWords = {
      wordsAcross: words.wordsAcross.map((word) => ({
        ...word,
        id: word.id,
        clue:
          (wordsAcross as AscCrosswordPuzzleWordCreate[])?.find(
            (w) => w?.beginningCellNumber === word.beginningCellNumber
          )?.clue || "",
      })),
      wordsDown: words.wordsDown.map((word) => ({
        ...word,
        id: word.id,
        clue:
          (wordsDown as AscCrosswordPuzzleWordCreate[])?.find(
            (w) => w?.beginningCellNumber === word.beginningCellNumber
          )?.clue || "",
      })),
    };
    setGenerateWords(generateWords);
    if (!error) {
      onGenerate?.(generateWords, createModeSize);
    }
  }

  return (
    <div
      data-testid="asc-crossword-puzzle"
      className={classNames(styles.ground, className)}
    >
      <AscCrosswordPuzzleBoard
        mode={mode}
        wordsAcross={wordsAcross}
        wordsDown={wordsDown}
        onChange={handleOnChange}
        onGenerate={handleOnGenerate}
        size={mode === "create" ? createModeSize : size}
        disabled={disabled}
        onError={onError}
      />
      <div className={styles.infoContainer} {...ignoreAttribute.contrast}>
        {mode === "create" ? (
          <AscSelectInput
            value={createModeSize}
            multiple={false}
            onChange={handleOnSizeChange}
            className={styles.sizeSelector}
          >
            <AscSelectInput.Option
              value={AscCrosswordPuzzleBoardSize.SIX_BY_SIX}
              label="6 x 6"
            />
            <AscSelectInput.Option
              value={AscCrosswordPuzzleBoardSize.EIGHT_BY_EIGHT}
              label="8 x 8"
            />
            <AscSelectInput.Option
              value={AscCrosswordPuzzleBoardSize.TEN_BY_TEN}
              label="10 x 10"
            />
            <AscSelectInput.Option
              value={AscCrosswordPuzzleBoardSize.TWELVE_BY_TWELVE}
              label="12 x 12"
            />
          </AscSelectInput>
        ) : null}
        {mode === "create" ? (
          <AscCrosswordPuzzleClueList
            cluesAcross={generateWords?.wordsAcross}
            cluesDown={generateWords?.wordsDown}
          />
        ) : (
          <AscCrosswordPuzzleClueList
            cluesAcross={wordsAcross as AscCrosswordPuzzleWordSolve[]}
            cluesDown={wordsDown as AscCrosswordPuzzleWordSolve[]}
          />
        )}
      </div>
    </div>
  );
}
